import { useCallback, useEffect, useRef, useState } from "react";
import { useChat } from "@ai-sdk/react";
import { DefaultChatTransport, type UIMessage } from "ai";
import { Bot, Square, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Conversation,
  ConversationContent,
  ConversationEmptyState,
  ConversationScrollButton,
} from "@/components/ai-elements/conversation";
import { Message, MessageContent, MessageResponse } from "@/components/ai-elements/message";
import {
  PromptInput,
  PromptInputFooter,
  PromptInputSubmit,
  PromptInputTextarea,
} from "@/components/ai-elements/prompt-input";
import { Shimmer } from "@/components/ai-elements/shimmer";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export const AiAssistant = () => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const loadedRef = useRef(false);
  const bodyRef = useRef<HTMLDivElement | null>(null);

  const { messages, sendMessage, status, error, stop, setMessages } = useChat({
    id: "sabi-ai-assistant",
    transport: new DefaultChatTransport({
      api: "https://fdcqcibrdsuvmdwagulk.supabase.co/functions/v1/ai-chat",
      headers: async () => {
        const { data } = await supabase.auth.getSession();
        const token = data.session?.access_token;
        return token ? { Authorization: `Bearer ${token}` } : {};
      },
    }),
  });

  // Load the saved conversation once, the first time the chat is opened.
  useEffect(() => {
    if (!open || loadedRef.current || !user) return;
    loadedRef.current = true;
    supabase
      .from("ai_chat_messages")
      .select("id, role, parts, created_at")
      .eq("user_id", user.id)
      .order("created_at", { ascending: true })
      .then(({ data, error: loadError }) => {
        if (loadError) {
          console.error("Failed to load AI chat history:", loadError);
          return;
        }
        if (data?.length) {
          setMessages(
            data.map((row) => ({ id: row.id, role: row.role, parts: row.parts } as UIMessage)),
          );
        }
      });
  }, [open, user, setMessages]);

  // Keep the composer focused across sending and stream completion.
  useEffect(() => {
    if (!open) return;
    const textarea = bodyRef.current?.querySelector("textarea");
    if (textarea) textarea.focus();
  }, [open, status, messages.length]);

  const handleFocus = useCallback(() => {
    const textarea = bodyRef.current?.querySelector("textarea");
    if (textarea) textarea.focus();
  }, []);

  const handleSubmit = useCallback(
    (message: { text?: string }) => {
      const text = message.text?.trim();
      if (!text) return;
      sendMessage({ text });
      requestAnimationFrame(handleFocus);
    },
    [sendMessage, handleFocus],
  );

  if (!user) return null;

  return (
    <>
      {open && (
        <div
          className="fixed bottom-24 right-4 z-50 flex h-[min(600px,70vh)] w-[calc(100vw-2rem)] max-w-sm flex-col overflow-hidden rounded-xl border bg-background shadow-2xl sm:right-6"
          role="dialog"
          aria-label="Sabi AI assistant chat"
        >
          <div className="flex items-center gap-3 border-b bg-primary/5 px-4 py-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10">
              <Bot className="h-5 w-5 text-primary" aria-hidden="true" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold leading-tight">Sabi — AI Assistant</p>
              <p className="text-xs text-muted-foreground leading-tight">
                Orders, delivery, payments & selling
              </p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => setOpen(false)}
              aria-label="Close AI assistant"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          <div ref={bodyRef} className="flex min-h-0 flex-1 flex-col">
            <Conversation className="flex-1">
              <ConversationContent className="p-4">
                {messages.length === 0 ? (
                  <ConversationEmptyState
                    icon={<Bot className="h-6 w-6" aria-hidden="true" />}
                    title="Hi, I'm Sabi"
                    description="Ask me anything about buying, orders, delivery, payments or selling on SabiTradeNG."
                  />
                ) : (
                  messages.map((m) => {
                    const text = m.parts
                      .filter((p) => p.type === "text")
                      .map((p) => p.text)
                      .join("");
                    return (
                      <Message from={m.role} key={m.id}>
                        <MessageContent
                          className={
                            m.role === "user"
                              ? "bg-primary text-primary-foreground"
                              : "bg-transparent"
                          }
                        >
                          {m.role === "assistant" ? (
                            <MessageResponse>{text}</MessageResponse>
                          ) : (
                            <span className="whitespace-pre-wrap">{text}</span>
                          )}
                        </MessageContent>
                      </Message>
                    );
                  })
                )}
                {status === "submitted" && (
                  <div className="px-2 py-2 text-sm text-muted-foreground">
                    <Shimmer>Sabi is thinking…</Shimmer>
                  </div>
                )}
                {error && (
                  <div className="px-2 py-2 text-sm text-destructive">
                    {error.message || "Something went wrong. Please try again."}
                  </div>
                )}
              </ConversationContent>
              <ConversationScrollButton />
            </Conversation>

            <div className="border-t p-2">
              <PromptInput onSubmit={handleSubmit}>
                <PromptInputTextarea
                  placeholder="Ask anything…"
                  rows={1}
                  className="max-h-32"
                  aria-label="Message Sabi AI assistant"
                />
                <PromptInputFooter className="justify-end px-2 py-1">
                  {status === "streaming" || status === "submitted" ? (
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-8 w-8"
                      onClick={() => stop()}
                      aria-label="Stop generating"
                    >
                      <Square className="h-4 w-4" />
                    </Button>
                  ) : (
                    <PromptInputSubmit status="ready" aria-label="Send message" />
                  )}
                </PromptInputFooter>
              </PromptInput>
            </div>
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="fixed bottom-6 right-6 z-50 rounded-full bg-primary p-4 text-primary-foreground shadow-lg transition-all hover:scale-105 hover:shadow-xl"
        aria-label={open ? "Close AI assistant" : "Open Sabi AI assistant"}
      >
        {open ? <X className="h-6 w-6" /> : <Bot className="h-6 w-6" />}
      </button>
    </>
  );
};
